import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'hooks/useDebounce';

import { stockListSelector } from 'store/stock/selectors';
import { priceTypesSelector } from 'store/auth/selectors';
import { interestStatisticSelector } from 'store/interest/selectors';
import { Interest } from 'store/interest/actions';
import { Stock } from 'store/stock/actions';
import { Auth } from 'store/auth/actions';

import { InputIcon } from 'components/Input';
import { Portal } from 'components/Portal';
import { Loader } from 'components/Loader';
import { InputRangeDatePicker } from 'components/InputRangeDatePicker/InputRangeDatePicker';
import { InputAutoComplete } from 'components/InputAutoComplete/InputAutoComplete';

import './style.scss';
import { useFirstRender } from 'hooks/useFirstRender';

const ModalStatisticInterest = ({ onClose }) => {
  const dispatch = useDispatch();

  const [calendarVisible, setCalendarVisible] = useState(false);
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);

  const [stock, setStock] = useState(null);
  const [priceType, setPriceType] = useState(null);

  const [fromDate, setFrom] = useState(undefined);
  const [toDate, setTo] = useState(undefined);

  const priceTypeList = useSelector(priceTypesSelector);
  const interest = useSelector(interestStatisticSelector);
  const stockList = useSelector(stockListSelector);
  const firstRender = useFirstRender();
  const debouncedText = useDebounce(text, 500, true);

  useEffect(() => {
    if (stockList === null) {
      dispatch(Stock.getStockList());
    }
    if (priceTypeList.length === 0) {
      dispatch(Auth.getPriceTypesList());
    }
    dispatch(Interest.getInterestStatisticList());
  }, [onClose]);

  useEffect(() => {
    if (firstRender) {
      return;
    }
    handleFilter();
  }, [priceType, stock, debouncedText, toDate, fromDate]);


  const handleText = (value) => {
    setText(value);
  };

  const handleStock = (value) => {
    setPriceType(null);
    setStock(value);
  };

  const handlePriceType = (value) => {
    setStock(null);
    setPriceType(value);
  };

  const handleFilter = () => {
    setCalendarVisible(false);
    const value = {};
    if (text) {
      value.item_name = text;
    }

    if (stock) {
      value.stockID = stock.stockID1C;
    }

    if (priceType) {
      value.city = priceType.priceTypeID1C;
    }

    if (!fromDate & !toDate) {
      dispatch(Interest.getInterestStatisticList(value));
    } else {
      dispatch(
        Interest.getInterestStatisticList({
          ...value,
          dateTime_before: new Date(+toDate).toISOString(),
          dateTime_after: new Date(+fromDate).toISOString(),
        })
      );
    }
  };

  const handleFilterByDate = ({ from, to } = {}) => {
    if (from & to) {
      setTo(to);
      setFrom(from);
    }
    if (!from & !to) {
      setTo(undefined);
      setFrom(undefined);
    }
  };

  return (
    <Portal id="modal-statistic" className="interest-statistic dropdownScroll" onClose={onClose}>
      <div className="modal-title">
        <div className="header-title-interest-statistic">
          <b>Статистика интересов клиентов</b>
        </div>
      </div>
      <div className="interest-filter-modal">
        <div className="instruments-search-panel">
          <InputIcon icon="search" value={text} onChange={handleText} placeholder="Наименование..." />
        </div>
        <div className="instruments-search-panel">
          <InputAutoComplete
            icon="search"
            selected={{ value: stock?.stockID1C || '', text: stock?.stockName || '' }}
            list={stockList.map((stockElem) => ({
              text: stockElem.stockName,
              value: stockElem.stockID1C,
              returning: stockElem,
            }))}
            size="90px"
            onChange={handleStock}
            placeholder="Склад..."
          />
        </div>
        <div className="instruments-search-panel">
          <InputAutoComplete
            icon="search"
            selected={{ value: priceType?.priceTypeID1C || '', text: priceType?.typeName || '' }}
            list={priceTypeList.map((elem) => ({
              text: elem.typeName,
              value: elem.priceTypeID1C,
              returning: elem,
            }))}
            size="90px"
            onChange={handlePriceType}
            placeholder="Город..."
          />
        </div>
        <div className="instruments-search-panel">
          <InputRangeDatePicker
            handleFilter={handleFilterByDate}
            boxShadow={false}
            relative={true}
            close={() => setCalendarVisible(false)}
          />
        </div>
      </div>
      <div className="table-interest-container dropdownScroll">
        <table className="table-interest dropdownScroll">
          <thead>
            <tr className="table-interest-tr">
              <th className="interest-header" style={{ width: '1000px', textAlign: 'left' }}>
                Наименование
              </th>
              <th className="interest-header" style={{ width: '100px', textAlign: 'center' }}>
                Интересы
              </th>
              <th className="interest-header" style={{ width: '20px', textAlign: 'left' }}></th>
            </tr>
          </thead>
          <tbody className="body-interest-list dropdownScroll">
            {interest &&
              interest.map((element) => {
                return (
                  <tr key={element.item_name}>
                    <th className="interest-row text-light" style={{ width: '1000px', textAlign: 'left' }}>
                      {element.item_name}
                    </th>
                    <th className="interest-row text-light" style={{ width: '100px', textAlign: 'center' }}>
                      {element.interest_count}
                    </th>
                    <th className="interest-row text-light" style={{ width: '20px', textAlign: 'left' }}></th>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </Portal>
  );
};

ModalStatisticInterest.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export { ModalStatisticInterest };
